import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { galleryApi } from '../lib/api'
import { galleryCache } from '../lib/cache'

const PageContainer = styled.div`
  padding-top: 110px;
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f9f4 0%, #f8fafc 40%, #ffffff 100%);
`

const Hero = styled.section`
  padding: 3rem 1.25rem 2rem;
  text-align: center;

  h1 {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3rem);
    font-family: 'Playfair Display', serif;
    color: #0f172a;
  }

  p {
    margin: 0.9rem auto 0;
    max-width: 760px;
    color: #334155;
    line-height: 1.6;
    font-family: 'Lato', sans-serif;
  }
`

const Controls = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0.9rem;
  padding: 0 1.25rem 1.5rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
  font-size: 1rem;
`

const Select = styled.select`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.8rem 0.9rem;
  font-size: 1rem;
  background: #fff;
`

const Content = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.25rem 3rem;
`

const Info = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  font-family: 'Lato', sans-serif;
`

const LoadingPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 1rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
  font-size: 0.85rem;
  font-family: 'Lato', sans-serif;
`

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #2563eb;
  animation: pulse 1s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 0.35;
      transform: scale(0.9);
    }
    50% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0.35;
      transform: scale(0.9);
    }
  }
`

const Error = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  padding: 0.8rem 0.9rem;
  border-radius: 10px;
  margin-bottom: 1rem;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`

const Card = styled.article`
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    display: block;
    background: #e2e8f0;
  }
`

const CardBody = styled.div`
  padding: 0.85rem;

  h3 {
    margin: 0;
    font-size: 1rem;
    color: #111827;
    font-family: 'Playfair Display', serif;
  }

  p {
    margin: 0.45rem 0 0;
    color: #475569;
    line-height: 1.5;
    font-size: 0.93rem;
    font-family: 'Lato', sans-serif;
  }
`

function GalleryPage() {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [cacheMeta, setCacheMeta] = useState({ isFresh: false, cachedAt: null })
  const [imagesTotal, setImagesTotal] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(0)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        const cached = await galleryCache.readList()
        if (mounted && cached.value && Array.isArray(cached.value.items)) {
          setItems(cached.value.items)
          setCacheMeta({ isFresh: cached.isFresh, cachedAt: cached.cachedAt })
          setIsLoading(false)
        }
      } catch (_error) {
        // cache failures should not block API load
      }

      try {
        const payload = await galleryApi.list({ page: 1, limit: 100 })
        if (!mounted) return

        const freshItems = Array.isArray(payload.items) ? payload.items : []
        setItems(freshItems)
        setError('')
        setIsLoading(false)
        setCacheMeta({ isFresh: true, cachedAt: Date.now() })
        await galleryCache.writeList(payload)
      } catch (apiError) {
        if (!mounted) return
        setError(apiError.message || 'Failed to load gallery')
        setIsLoading(false)
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [])

  const categories = useMemo(() => {
    const map = new Set()
    items.forEach(item => {
      if (item.category) map.add(item.category)
    })
    return Array.from(map)
  }, [items])

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()

    return items.filter(item => {
      const categoryMatch = category === 'all' ? true : item.category === category
      const searchText = `${item.caption || ''} ${item.category || ''}`.toLowerCase()
      const queryMatch = query ? searchText.includes(query) : true
      return categoryMatch && queryMatch
    })
  }, [items, search, category])

  useEffect(() => {
    setImagesTotal(filteredItems.length)
    setImagesLoaded(0)
  }, [filteredItems])

  const handleImageSettled = () => {
    setImagesLoaded(prev => prev + 1)
  }

  const cacheState = cacheMeta.cachedAt
    ? cacheMeta.isFresh
      ? 'Fresh data loaded from API.'
      : 'Showing cached data while refreshing in background.'
    : 'No cache available yet.'
  const settledImages = Math.min(imagesLoaded, imagesTotal)
  const pendingImages = Math.max(imagesTotal - settledImages, 0)

  return (
    <PageContainer>
      <Hero>
        <h1>Gallery</h1>
        <p>
          Explore selected Sunbold field projects and engineering highlights. Images are cached locally for faster repeat browsing.
        </p>
      </Hero>

      <Controls>
        <Input
          type="search"
          placeholder="Search by caption or category"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />

        <Select value={category} onChange={event => setCategory(event.target.value)}>
          <option value="all">All categories</option>
          {categories.map(name => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </Controls>

      <Content>
        <Info>
          {isLoading ? 'Loading gallery...' : `${filteredItems.length} items`} • {cacheState}
        </Info>
        <Info>Images loaded: {settledImages}/{imagesTotal}</Info>

        {isLoading || pendingImages > 0 ? (
          <LoadingPill>
            <Dot />
            {isLoading ? 'Loading gallery data...' : `Loading image assets (${pendingImages} left)...`}
          </LoadingPill>
        ) : null}

        {error ? <Error>{error}</Error> : null}

        <Grid>
          {filteredItems.map(item => (
            <Card key={item._id || item.image}>
              <img
                src={item.image}
                alt={item.caption || 'Gallery item'}
                loading="lazy"
                onLoad={handleImageSettled}
                onError={handleImageSettled}
              />
              <CardBody>
                <h3>{item.category || 'General'}</h3>
                <p>{item.caption || 'No caption provided.'}</p>
              </CardBody>
            </Card>
          ))}
        </Grid>
      </Content>
    </PageContainer>
  )
}

export default GalleryPage
